import { useState, useCallback } from 'react';
import { Variants, MotionProps } from 'framer-motion';
import {
    HOVER_VARIANTS,
    BUTTON_VARIANTS,
    BUBBLE_VARIANTS,
    PANEL_SLIDE_VARIANTS,
    TRANSITIONS,
    SPRING_CONFIGS,
    PERFORMANCE_PROPS
} from '../utils/animationConfig';

export type AnimationType = 
    | 'hover' 
    | 'button' 
    | 'bubble' 
    | 'panel' 
    | 'custom';

export interface UseElementAnimationsOptions {
    type: AnimationType;
    customVariants?: Variants;
    enableHover?: boolean;
    enableTap?: boolean;
    isOpen?: boolean;
    onHoverStart?: () => void;
    onHoverEnd?: () => void;
    onTapStart?: () => void;
    onTapEnd?: () => void;
}

export interface UseElementAnimationsResult {
    motionProps: MotionProps;
    
    isHovered: boolean;
    isTapped: boolean;
    
    handleHoverStart: () => void;
    handleHoverEnd: () => void;
    handleTapStart: () => void;
    handleTapEnd: () => void;
}

export const useElementAnimations = (
    options: UseElementAnimationsOptions
): UseElementAnimationsResult => {
    const {
        type,
        customVariants,
        enableHover = true,
        enableTap = true,
        isOpen = false,
        onHoverStart,
        onHoverEnd,
        onTapStart,
        onTapEnd
    } = options;

    const [isHovered, setIsHovered] = useState(false);
    const [isTapped, setIsTapped] = useState(false);

    const getVariants = useCallback((): Variants => {
        if (customVariants) {
            return customVariants;
        }

        switch (type) {
            case 'hover':
                return HOVER_VARIANTS;
            case 'button':
                return BUTTON_VARIANTS;
            case 'bubble':
                return BUBBLE_VARIANTS;
            case 'panel':
                return PANEL_SLIDE_VARIANTS;
            default:
                return HOVER_VARIANTS;
        }
    }, [type, customVariants]);

    const variants = getVariants();

    const handleHoverStart = useCallback(() => {
        if (!enableHover) {
            return;
        }
        setIsHovered(true);
        onHoverStart?.();
    }, [enableHover, onHoverStart]);

    const handleHoverEnd = useCallback(() => {
        if (!enableHover) {
            return;
        }
        setIsHovered(false);
        onHoverEnd?.();
    }, [enableHover, onHoverEnd]);

    const handleTapStart = useCallback(() => {
        if (!enableTap) {
            return;
        }
        setIsTapped(true);
        onTapStart?.();
    }, [enableTap, onTapStart]);

    const handleTapEnd = useCallback(() => {
        if (!enableTap) {
            return;
        }
        setIsTapped(false);
        onTapEnd?.();
    }, [enableTap, onTapEnd]);

    const getCurrentAnimationState = useCallback(() => {
        if (type === 'panel') {
            return isOpen ? 'visible' : 'hidden';
        }
        
        if (type === 'bubble' && isOpen) {
            return 'open';
        }
        
        if (isTapped && variants.tap) {
            return 'tap';
        }
        
        if (isHovered && variants.hover) {
            return 'hover';
        }
        
        return variants.initial ? 'initial' : 'visible';
    }, [type, isOpen, isTapped, isHovered, variants]);

    const motionProps: MotionProps = {
        variants,
        initial: variants.hidden ? 'hidden' : 'initial',
        animate: getCurrentAnimationState() as any,
        exit: variants.exit ? 'exit' : 'hidden',
        ...PERFORMANCE_PROPS,
        transition: type === 'button' ? TRANSITIONS.FAST_EASE_OUT : SPRING_CONFIGS.SMOOTH
    };

    if (enableHover) {
        motionProps.onHoverStart = handleHoverStart;
        motionProps.onHoverEnd = handleHoverEnd;
    }
    if (enableTap) {
        motionProps.onTapStart = handleTapStart;
        motionProps.onTap = handleTapEnd;
    }

    return {
        motionProps,
        isHovered,
        isTapped,
        handleHoverStart,
        handleHoverEnd,
        handleTapStart,
        handleTapEnd
    };
};

// Convenience hooks for specific animation types
export const useBubbleAnimation = (options: Omit<UseElementAnimationsOptions, 'type'> = {}) => 
    useElementAnimations({ ...options, type: 'bubble' });

export const usePanelAnimation = (options: Omit<UseElementAnimationsOptions, 'type'> = {}) => 
    useElementAnimations({ ...options, type: 'panel' });
